// Tiny in-app translation layer for the small handful of user-facing strings
// kids see during a lesson. The translator is keyed by the English source
// string so call sites stay readable: `t("Listen and pick the picture")`.
//
// Reads the current player's `learnLanguage` from the loaded profile (passed
// via useLocalProfile / loadProfile). When learnLanguage === "vi", strings
// in the VI map are returned; for anything not in the map we fall back to
// the English source.

import { useMemo } from "react";
import { useLocalProfile } from "./useLocalProfile";

type Lang = "en" | "vi";

// Vietnamese translations for every short UI string a kid sees during a
// lesson + the surrounding results screen. Long-form lobby and settings
// strings stay English for now — those are read by adults more than kids.
const VI: Record<string, string> = {
  // WordIntro card flipper
  "Word {n} of {total}": "Từ {n} / {total}",
  "Skip ↠": "Bỏ qua ↠",
  "Next →": "Tiếp →",
  "Start running →": "Bắt đầu chạy →",
  "Replay audio": "Nghe lại",
  "Hear sentence": "Nghe câu",

  // Challenge tags (top-left badge)
  "Listen": "Nghe",
  "Picture sound": "Âm thanh hình",
  "Look": "Nhìn",
  "Fill the gap": "Điền vào chỗ trống",
  "Read": "Đọc",

  // Challenge prompts
  "Listen and pick the picture": "Nghe và chọn hình",
  "Listen and pick the word": "Nghe và chọn từ",
  "Tap a bubble to hear it": "Chạm vào bong bóng để nghe",
  "Tap the same bubble again to choose it": "Chạm lần nữa để chọn",
  "Look and pick the word": "Nhìn và chọn từ",
  "Read and pick the picture": "Đọc và chọn hình",
  "Pick the missing word": "Chọn từ còn thiếu",

  // Shell branding
  "⚡ WORD BOOST": "⚡ TĂNG TỐC TỪ",
  "🔊 Replay": "🔊 Nghe lại",

  // Feedback after pick
  "The answer was…": "Câu trả lời là…",
  "Correct!": "Chính xác!",
  "Try again": "Thử lại",

  // Results screen
  "Finished!": "Hoàn thành!",
  "Perfect run!": "Hoàn hảo!",
  "Next level →": "Cấp tiếp theo →",
  "↻ Play again": "↻ Chơi lại",
  "← Pick another level": "← Chọn cấp khác",
  "🏠 Main menu": "🏠 Trang chính",
  "Words": "Từ",
  "Time": "Thời gian",
  "Score": "Điểm",
  "coins": "xu",
  "Level": "Cấp",
  "⭐ LEVEL UP! ⭐": "⭐ LÊN CẤP! ⭐",
  "Returning to lobby in {n}…": "Quay lại sảnh trong {n}…",
  "Returning to lobby…": "Đang quay lại sảnh…",

  // Place announcements in battle (race finish)
  "First place!": "Hạng nhất!",
  "Second place!": "Hạng nhì!",
  "Third place!": "Hạng ba!",
  "{n}th place": "Hạng {n}",
  "You won!": "Bạn thắng!",

  // Pause overlay
  "Paused": "Tạm dừng",
  "▶ Resume": "▶ Tiếp tục",
  "⚙ Settings": "⚙ Cài đặt",
  "← Exit": "← Thoát",
};

const TABLES: Record<Lang, Record<string, string>> = {
  en: {},
  vi: VI,
};

export type Translator = (key: string, vars?: Record<string, string | number>) => string;

/** Build a translator function for the given language. Substitutes `{name}`
 *  placeholders from the vars map. Falls back to the English source if no
 *  translation is registered for that key. */
export function makeTranslator(lang: Lang | undefined): Translator {
  const table = TABLES[lang ?? "en"];
  return (key: string, vars?: Record<string, string | number>) => {
    const raw = table[key] ?? key;
    if (!vars) return raw;
    return raw.replace(/\{(\w+)\}/g, (_, name) => String(vars[name] ?? ""));
  };
}

/** React hook — returns a translator function bound to the current profile's
 *  learnLanguage. Memoized so consumers don't get a new fn each render. */
export function useT(): Translator {
  const { profile } = useLocalProfile();
  const lang = (profile?.learnLanguage ?? "en") as Lang;
  return useMemo(() => makeTranslator(lang), [lang]);
}
